import { Component } from '@theme/component';
import { lockScroll, unlockScroll } from '@theme/utilities';

const DISMISSED = 'henry-newsletter-dismissed-until';
const SUBSCRIBED = 'henry-newsletter-subscribed';
const PENDING = 'henry-newsletter-pending';

// Storage can be unavailable in private browsing; the form must still work.
function read(storage, key) {
  try { return window[storage].getItem(key); } catch { return null; }
}
function write(storage, key, value) {
  try { window[storage].setItem(key, String(value)); } catch { /* Optional preference only. */ }
}

/** Sitewide, editor-configurable newsletter with native Shopify submission. */
class HenryNewsletter extends Component {
  requiredRefs = ['dialog', 'teaser'];
  #controller;
  #timer;
  #previousFocus;
  #submitted = false;
  #form;

  connectedCallback() {
    super.connectedCallback();
    this.#controller = new AbortController();
    const { signal } = this.#controller;
    const { dialog } = this.refs;
    this.#form = this.querySelector('form');
    if (!this.#form) return;

    dialog.addEventListener('cancel', (event) => {
      event.preventDefault();
      this.closeDialog();
    }, { signal });
    dialog.addEventListener('click', (event) => {
      if (event.target !== dialog) return;
      const rect = dialog.getBoundingClientRect();
      if (event.clientX < rect.left || event.clientX > rect.right || event.clientY < rect.top || event.clientY > rect.bottom) this.closeDialog();
    }, { signal });
    dialog.addEventListener('close', () => {
      unlockScroll(dialog);
      this.refs.teaser.hidden = this.#subscribed();
      if (this.#previousFocus instanceof HTMLElement && this.#previousFocus !== document.body) {
        this.#previousFocus.focus({ preventScroll: true });
      } else if (!this.refs.teaser.hidden) {
        this.refs.teaser.focus({ preventScroll: true });
      }
    }, { signal });
    this.#form.addEventListener('submit', () => {
      // Keep Shopify's regular POST, spam protection and server-side error handling.
      write('sessionStorage', PENDING, Date.now());
    }, { signal });
    document.addEventListener('shopify:section:select', (event) => {
      if (event.detail?.sectionId === this.dataset.sectionId) this.openDialog();
    }, { signal });

    const result = this.querySelector('[data-newsletter-result]');
    const pending = Number(read('sessionStorage', PENDING));
    const ours = location.hash === `#${this.#form.id}` || (pending > 0 && Date.now() - pending < 30 * 60 * 1000);
    if (result && ours) {
      write('sessionStorage', PENDING, 0);
      if (result.dataset.newsletterResult === 'success') {
        this.#submitted = true;
        write('localStorage', SUBSCRIBED, 'true');
      }
      this.openDialog();
      return;
    }
    this.refs.teaser.hidden = this.#subscribed();
    if (!this.#subscribed() && !window.Shopify?.designMode && Number(read('localStorage', DISMISSED)) < Date.now()) {
      this.#timer = window.setTimeout(() => this.#autoOpen(), Number(this.dataset.delay) || 8000);
    }
  }

  disconnectedCallback() {
    window.clearTimeout(this.#timer);
    this.#controller?.abort();
    if (this.refs.dialog) {
      unlockScroll(this.refs.dialog);
      this.refs.dialog.close();
    }
    super.disconnectedCallback();
  }

  #subscribed() {
    return this.#submitted || this.dataset.subscribed === 'true' || read('localStorage', SUBSCRIBED) === 'true';
  }

  #autoOpen() {
    if (this.#subscribed() || Number(read('localStorage', DISMISSED)) > Date.now()) return;
    const activeInput = document.activeElement?.matches('input, textarea, select, [contenteditable="true"]');
    const overlay = [...document.querySelectorAll('dialog[open], [role="dialog"], #shopify-pc__banner')]
      .some((node) => node !== this.refs.dialog && node.getBoundingClientRect().height > 0 && getComputedStyle(node).visibility !== 'hidden');
    if (document.hidden || activeInput || overlay || document.documentElement.hasAttribute('scroll-lock')) {
      this.#timer = window.setTimeout(() => this.#autoOpen(), 2000);
      return;
    }
    this.openDialog();
  }

  /** Open with a native modal for focus containment and background inertness. */
  openDialog() {
    window.clearTimeout(this.#timer);
    const { dialog, teaser } = this.refs;
    if (dialog.open || !dialog.isConnected) return;
    this.#previousFocus = document.activeElement;
    dialog.showModal();
    lockScroll(dialog);
    teaser.hidden = true;
  }

  /** Remember dismissal and return visitors to their previous place. */
  closeDialog() {
    const { dialog } = this.refs;
    if (!dialog.open) return;
    write('localStorage', DISMISSED, Date.now() + Number(this.dataset.dismissDays) * 86400000);
    dialog.close();
  }
}

if (!customElements.get('henry-newsletter')) customElements.define('henry-newsletter', HenryNewsletter);
